import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, isWard, isXaphuongTinhThanh, notify } from '~/utils/helper';
import { CloseIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { IMasterDataItem } from '~/utils/interface/master';
import locationApi from '~/apis/location';
import personValidation from '~/validation/person';
import { useAppDispatch } from '~/redux/hooks';
import { IEditPerson, IPerson } from '~/utils/interface/person';
import deviceApi from '~/apis/device';
import dayjs from 'dayjs';
import BaseRow from '~/components/common/base-row/BaseRow';
import { GenderEnum, ImeiEnum } from '~/utils/enum';
import { commonActions } from '~/thunks/common/commonSlice';
import { IUser } from '~/utils/interface/user';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import { handleCreatePerson, handleUpdatePerson } from '~/thunks/person/personThunk';

type EditLocationDialogProps = {
  data?: IPerson;
  open: boolean;
  mode: string;
  currentUser?: IUser;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IEditPerson = {
  full_name: '',
  cccd: '',
  gender: GenderEnum.MALE,
  simno: '',
  email: '',
  birth_date: '',
  place_of_origin: '',
  current_address: '',
  province_id: '',
  ward_id: '',
  imei: '',
  assign_type: ImeiEnum.ASSIGNED,
};

const EditLocationDialog = (props: EditLocationDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/person');

  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [unassignedImeiOptions, setUnassignedImeiOptions] = useState<IMasterDataItem[]>([]);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    enableReinitialize: true,
    validationSchema: personValidation(t).edit,
    onSubmit: async (values) => {
      dispatch(commonActions.setIsLoading(true));
      const { assign_type, ...dataSubmit } = values;

      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreatePerson(dataSubmit)).unwrap();
          notify('success', t('addSuccess', { ns: 'admin/common', name: t('person1') }));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
      }
      if (props.mode === DIALOG_MODE_EDIT && props.data?.id) {
        try {
          await dispatch(handleUpdatePerson({ id: props.data?.id, params: dataSubmit })).unwrap();
          notify('success', t('updateSuccess', { ns: 'admin/common', name: t('person1') }));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
      }
    },
  });

  useEffect(() => {
    getListProvinces();
    getUnassignedImeis();
  }, [props.open]);

  useEffect(() => {
    if (form.values.province_id) {
      (async () => {
        try {
          const response = await locationApi.getListWards({ province: form.values.province_id ?? '' });
          setWardOptions(
            response.data.map((item) => {
              return {
                id: item.code,
                name: item.name,
                data0: item.name,
                data1: item.name_with_type,
                data2: item.type,
                data3: item.parent_code,
              };
            })
          );
        } catch (error) {
          setWardOptions([]);
          handleApiError(error);
        }
      })();
    } else setWardOptions([]);
  }, [form.values.province_id]);

  useEffect(() => {
    if (isXaphuongTinhThanh(cast(props.currentUser?.roleid)))
      form.setFieldValue('province_id', String(props?.currentUser?.province_id));
    form.setFieldValue('ward_id', String(props?.currentUser?.ward_id || ''));

    if (props.open && props.data && props.mode === DIALOG_MODE_EDIT) {
      const data = props.data;

      form.setValues({
        cccd: cast(data.cccd),
        full_name: cast(data.full_name),
        birth_date: data.birth_date ? dayjs(data.birth_date).format('YYYY-MM-DD') : '',
        gender: data.gender,
        place_of_origin: cast(data.place_of_origin),
        current_address: cast(data.current_address),
        email: cast(data.email),
        simno: cast(data.simno),
        imei: cast(data.imei),
        province_id: data.province_id ? String(data.province_id) : '',
        ward_id: data.ward_id ? String(data.ward_id) : '',
        assign_type: data.imei ? ImeiEnum.ASSIGNED : ImeiEnum.UNASSIGNED,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data]);

  const getListProvinces = async () => {
    try {
      const response = await locationApi.getListProvinces();
      setProvinceOptions(
        response.data.map((item) => {
          return {
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          };
        })
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  const getUnassignedImeis = async () => {
    try {
      const response = await deviceApi.getUnassignedImeis({});
      setUnassignedImeiOptions(
        response.data.result.map((item: any) => ({
          id: item,
          name: item,
        }))
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
      <DialogTitle className='bg-white text-xl font-semibold text-[#161A1F]'>
        {props.mode === DIALOG_MODE_EDIT ? t('edit') : t('create')}
        <IconButton className='p-1' onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component='form'>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('cccd.text')}</InputLabel>
            <TextField
              autoComplete='off'
              className='w-full rounded-xl'
              name='cccd'
              placeholder={t('cccd.placeholder')}
              value={form.values?.cccd ?? ''}
              onChange={form.handleChange}
              error={Boolean(form.errors?.cccd)}
              disabled={props.mode === DIALOG_MODE_EDIT}
              helperText={form.errors?.cccd}
              FormHelperTextProps={{
                style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
              }}
            />
          </Box>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('full_name.title')}</InputLabel>
            <TextField
              autoComplete='off'
              className='w-full rounded-xl'
              name='full_name'
              placeholder={t('full_name.placeholder')}
              value={form.values?.full_name ?? ''}
              onChange={form.handleChange}
              error={Boolean(form.errors?.full_name)}
              helperText={form.errors?.full_name}
              FormHelperTextProps={{
                style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
              }}
            />
          </Box>
          <BaseRow className='mb-2 gap-4'>
            <Box className='flex-1'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('birth_date.title')}</Typography>
              <BaseDatePicker
                className='w-full'
                portalId='datePicker-portalId'
                value={form.values.birth_date}
                dateFormat='YYYY-MM-DD'
                placeholder={t('birth_date.placeholder')}
                onChange={(date) => {
                  form.setFieldValue('birth_date', dayjs(date).format('YYYY-MM-DD'));
                }}
                error={Boolean(form.errors?.birth_date)}
                helperText={form.errors?.birth_date}
                FormHelperTextProps={{
                  style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0, textAlign: 'right' },
                }}
              />
            </Box>
            <Box className='flex-1'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-3 ml-6'>{t('gender.title')}</Typography>
              <FormControl className='w-full justify-around'>
                <RadioGroup
                  row
                  name='gender'
                  value={form.values.gender}
                  onChange={form.handleChange}
                  aria-labelledby='gender-group-label'
                  className='w-full justify-around'
                >
                  <FormControlLabel
                    value={GenderEnum.FEMALE}
                    control={
                      <Radio
                        size='medium'
                        sx={{
                          color: '#7C8796',
                          '&.Mui-checked': {
                            color: '#2E89C8',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 24,
                          },
                        }}
                      />
                    }
                    label={t('gender.female')}
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: 16,
                        color: '#161A1F',
                      },
                    }}
                  />
                  <FormControlLabel
                    value={GenderEnum.MALE}
                    control={
                      <Radio
                        sx={{
                          color: '#7C8796',
                          '&.Mui-checked': {
                            color: '#2E89C8',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 24,
                          },
                        }}
                      />
                    }
                    label={t('gender.male')}
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: 16,
                        color: '#161A1F',
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </BaseRow>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('place_of_origin.title')}</InputLabel>
            <TextField
              autoComplete='off'
              className='w-full rounded-xl'
              name='place_of_origin'
              placeholder={t('place_of_origin.placeholder')}
              value={form.values?.place_of_origin ?? ''}
              onChange={form.handleChange}
              error={Boolean(form.errors?.place_of_origin)}
              helperText={form.errors?.place_of_origin}
              FormHelperTextProps={{
                style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
              }}
            />
          </Box>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('current_address.title')}</InputLabel>
            <TextField
              autoComplete='off'
              className='w-full rounded-xl'
              name='current_address'
              placeholder={t('current_address.placeholder')}
              value={form.values?.current_address ?? ''}
              onChange={form.handleChange}
              error={Boolean(form.errors?.current_address)}
              helperText={form.errors?.current_address}
              FormHelperTextProps={{
                style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
              }}
            />
          </Box>
          <BaseRow className='mb-2 gap-4'>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('email.title')}</InputLabel>
              <TextField
                autoComplete='off'
                className='w-full rounded-xl'
                name='email'
                placeholder={t('email.placeholder')}
                value={form.values?.email ?? ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.email)}
                helperText={form.errors?.email}
                FormHelperTextProps={{
                  style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
                }}
              />
            </Box>
            <Box className='flex-1'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('simno.title')}</InputLabel>
              <TextField
                autoComplete='off'
                className='w-full rounded-xl'
                name='simno'
                placeholder={t('simno.placeholder')}
                value={form.values?.simno ?? ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.simno)}
                helperText={form.errors?.simno}
                FormHelperTextProps={{
                  style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
                }}
              />
            </Box>
          </BaseRow>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('province_id')}</InputLabel>
            <BaseSelect
              options={
                isXaphuongTinhThanh(cast(props.currentUser?.roleid))
                  ? provinceOptions.filter(
                      (item: IMasterDataItem) => item.id === String(props.currentUser?.province_id)
                    )
                  : provinceOptions
              }
              value={form.values.province_id}
              placeholder={t('select_province_id')}
              name='province_id'
              isSearchEnable
              onChange={(e: any) => {
                form.handleChange(e);
                form.setFieldValue('ward_id', '');
              }}
              helperText={form.errors?.province_id}
              disabled={isXaphuongTinhThanh(cast(props.currentUser?.roleid))}
            />
          </Box>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('ward_id')}</InputLabel>
            <BaseSelect
              options={wardOptions}
              value={form.values.ward_id}
              placeholder={t('select_ward_id')}
              name='ward_id'
              isSearchEnable
              onChange={form.handleChange}
              helperText={form.errors?.ward_id}
              disabled={isWard(cast(props.currentUser?.roleid))}
            />
          </Box>

          {props.mode === DIALOG_MODE_CREATE && (
            <>
              <BaseRow className='gap-4'>
                <FormControl className='w-full justify-between'>
                  <RadioGroup
                    row
                    aria-labelledby='imei-group-label'
                    className='w-full justify-between'
                    name='assign_type'
                    value={form.values.assign_type ?? ImeiEnum.ASSIGNED}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      form.setFieldValue('assign_type', value);
                      if (value === ImeiEnum.UNASSIGNED) {
                        form.setFieldValue('imei', '');
                      }
                    }}
                  >
                    <FormControlLabel
                      value={ImeiEnum.ASSIGNED}
                      control={
                        <Radio
                          size='medium'
                          sx={{
                            color: '#7C8796',
                            '&.Mui-checked': {
                              color: '#2E89C8',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 24,
                            },
                          }}
                        />
                      }
                      label={t('assignStatus.title')}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: 16,
                          color: '#161A1F',
                        },
                      }}
                    />
                    <FormControlLabel
                      value={ImeiEnum.UNASSIGNED}
                      control={
                        <Radio
                          sx={{
                            color: '#7C8796',
                            '&.Mui-checked': {
                              color: '#2E89C8',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 24,
                            },
                          }}
                        />
                      }
                      label={t('assignStatus.noAssign')}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: 16,
                          color: '#161A1F',
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </BaseRow>
              <Box className='w-full'>
                {form.values.assign_type === ImeiEnum.ASSIGNED && (
                  <BaseSelect
                    options={unassignedImeiOptions}
                    value={form.values.imei ?? ''}
                    placeholder={t('assignStatus.placeholder')}
                    name='imei'
                    isSearchEnable
                    isDisplayAllOption
                    onChange={form.handleChange}
                    helperText={form.errors?.imei}
                  />
                )}
              </Box>
            </>
          )}
          <Box className='flex justify-between gap-3 mt-5'>
            <Button
              variant='outlined'
              className='w-full bg-[#D7DEE7] border-0 !border-inherit text-[#404854] text-base font-semibold'
              onClick={() => handleSetOpen(false)}
            >
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button
              variant='contained'
              className='w-full bg-[#123B6D] text-base font-semibold'
              onClick={() => form.handleSubmit()}
            >
              {props.mode === DIALOG_MODE_CREATE ? t('create2') : t('saveChange', { ns: 'admin/common' })}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(EditLocationDialog);
